import { useEffect } from 'react';

/**
 * useUnsavedChangesGuard — frontend data-loss stopgap (SESSION PR-D).
 *
 * Protects against silent loss of unsaved form edits. While `active` is true it:
 *
 *  1. Registers a `beforeunload` handler so the browser shows its native
 *     "Leave site?" prompt on refresh, tab/window close, address-bar
 *     navigation, AND full-document redirects (`window.location.href = '/login'`).
 *     That hard redirect is exactly how a session-expiry teardown silently
 *     destroyed unsaved Incident edits — and `beforeunload` is the only event
 *     that can intercept a full-document navigation.
 *
 *  2. Registers the guard in a module-level registry so code paths that perform
 *     an *in-app* navigation/logout — which do NOT fire `beforeunload` — can
 *     call {@link hasUnsavedChanges} and prompt the user before discarding work
 *     (e.g. `AuthContext.logout`).
 *
 * It never autosaves and never mutates business data — this is protection only.
 *
 * Scope note: `beforeunload` cannot intercept in-app React Router transitions.
 * This app uses classic `<BrowserRouter>`, so a full client-side navigation
 * blocker (`useBlocker`) requires a data-router migration and is deferred. This
 * hook is refresh/close/hard-redirect protection plus an in-app logout/redirect
 * confirm hook — not a universal route guard. A styled Save/Discard/Cancel
 * dialog (and "save before logout") is deferred to PR-D2.
 */

// Module-level registry of currently-active unsaved-changes guards. Each active
// guard holds a unique token; the registry is non-empty iff some mounted form
// has unsaved edits.
const activeGuards = new Set<symbol>();

/**
 * True while any mounted `useUnsavedChangesGuard(true)` is active — i.e. some
 * tracked form has unsaved user edits. Used by in-app logout/redirect paths
 * (which bypass `beforeunload`) to prompt before discarding work.
 */
export function hasUnsavedChanges(): boolean {
  return activeGuards.size > 0;
}

/**
 * Test-only: clears the registry. Not used in production code.
 * @internal
 */
export function __resetUnsavedChangesGuardForTests(): void {
  activeGuards.clear();
}

export function useUnsavedChangesGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;

    const token = Symbol('unsaved-changes-guard');
    activeGuards.add(token);

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      // Spec-defined opt-in: modern browsers ignore custom strings and show
      // their own native "Leave site?" dialog.
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      activeGuards.delete(token);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [active]);
}
